import { createReducer, on } from '@ngrx/store';
import * as CommissionActions from './commissions.actions';
import { Commission } from './commissions.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface CommissionsState {
    commissions: Commission[] | null;
    commission: Commission | null;
    isLoading: boolean;
    error: string | null;
    general_totals?: {
        total_gross: number | string;
        total_commission_amount: number | string;
        total_commission_rate: number | string;
    };
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialCommissionsState: CommissionsState = {
    commissions: [],
    commission: null,
    isLoading: false,
    error: null,
    general_totals: {
        total_gross: 0,
        total_commission_amount: 0,
        total_commission_rate: 0,
    },
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All Commissions Reducer
export const commissionsReducer = createReducer(
    initialCommissionsState,

    // On Load Commissions
    on(CommissionActions.loadCommissions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.loadCommissionsSuccess, (state, { commissions }) => ({
        ...state,
        isLoading: false,
        commissions: commissions,
    })),
    on(CommissionActions.loadCommissionsFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Load Single Commission
    on(CommissionActions.loadCommission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.loadCommissionSuccess, (state, { commission }) => ({
        ...state,
        isLoading: false,
        commission: commission,
    })),
    on(CommissionActions.loadCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(CommissionActions.createCommission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.createCommissionSuccess, (state, { commission }) => ({
        ...state,
        isLoading: false,
        commissions: [...(state.commissions || []), commission],
    })),
    on(CommissionActions.createCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(CommissionActions.updateCommission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.updateCommissionSuccess, (state, { commission }) => ({
        ...state,
        isLoading: false,
        commissions: [
            ...(state.commissions || []).map((c) =>
                c.location.id === commission.location.id ? commission : c
            ),
        ],
    })),
    on(CommissionActions.updateCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update Sales Rep Rate
    on(CommissionActions.updateSalesRepRate, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        CommissionActions.updateSalesRepRateSuccess,
        (state, { commission }) => ({
            ...state,
            isLoading: false,
            commissions: [
                ...(state.commissions || []).map((c) =>
                    c.location.id == commission.location.id ? commission : c
                ),
            ],
        })
    ),
    on(CommissionActions.updateSalesRepRateFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Reset Commission
    on(CommissionActions.resetCommission, (state) => ({
        ...state,
        isLoading: false,
    })),
    on(CommissionActions.resetCommissionSuccess, (state, { commission }) => ({
        ...state,
        isLoading: false,
        commissions: [
            ...(state.commissions || []).map((c) =>
                c.location.id === commission.location.id ? commission : c
            ),
        ],
    })),
    on(CommissionActions.resetCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete
    on(CommissionActions.deleteCommission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.deleteCommissionSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        commissions: [...(state.commissions || []).filter((c) => c.id !== id)],
    })),
    on(CommissionActions.deleteCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Parking | Others
    on(CommissionActions.deletePkOtCommission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommissionActions.deletePkOtCommissionSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        commissions: [...(state.commissions || []).filter((c) => c._id !== id)],
    })),
    on(CommissionActions.deletePkOtCommissionFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Sales Rep from Commission
    on(CommissionActions.deleteCommissionSalesRep, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        CommissionActions.deleteCommissionSalesRepSuccess,
        (state, { commission }) => ({
            ...state,
            isLoading: false,
            commissions: [
                ...(state.commissions || []).map((c) =>
                    c.location.id === commission.location.id ? commission : c
                ),
            ],
        })
    ),
    on(
        CommissionActions.deleteCommissionSalesRepFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
